import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { getPublicOrigin } from '../lib/publicUrl';
import { formatAddress, formatETH } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { BadgeDisplay, BadgesGrid } from '../components/Gamification';
import { 
    User,
    Trophy, 
    TrendingUp,
    TrendingDown,
    Coins,
    Target,
    Flame,
    Calendar,
    ExternalLink,
    Copy,
    Check,
    Share2,
    UserPlus,
    UserMinus,
    Activity,
    PieChart,
    Award,
    Star,
    Zap,
    Globe,
    Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { 
    ProfitChart, 
    WinLossChart, 
    CategoryPieChart 
} from '../components/charts/AnalyticsCharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProfilePage() {
    const { address: profileAddress } = useParams();
    const { address: connectedAddress, isConnected } = useWeb3();
    const [profile, setProfile] = useState(null);
    const [gamification, setGamification] = useState(null);
    const [stats, setStats] = useState(null);
    const [recentBets, setRecentBets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const [activeTab, setActiveTab] = useState('overview');

    const isOwnProfile = connectedAddress?.toLowerCase() === profileAddress?.toLowerCase();

    useEffect(() => {
        async function fetchData() {
            if (!profileAddress) return;
            setLoading(true);
            
            try {
                // Fetch gamification profile
                const gamRes = await fetch(`${API_URL}/api/gamification/${profileAddress}`);
                if (gamRes.ok) {
                    const gamData = await gamRes.json();
                    setGamification(gamData);
                }
                
                // Fetch advanced stats
                const statsRes = await fetch(`${API_URL}/api/users/${profileAddress}/advanced-stats`);
                if (statsRes.ok) {
                    const statsData = await statsRes.json();
                    setStats(statsData);
                }
                
                // Fetch recent bets
                const betsRes = await fetch(`${API_URL}/api/users/${profileAddress}/recent-bets?limit=5`);
                if (betsRes.ok) {
                    const betsData = await betsRes.json();
                    setRecentBets(betsData);
                }
                
                // Fetch follow status if connected
                if (connectedAddress && !isOwnProfile) {
                    const followRes = await fetch(`${API_URL}/api/social/${connectedAddress}/is-following/${profileAddress}`);
                    if (followRes.ok) {
                        const followData = await followRes.json();
                        setIsFollowing(followData.is_following);
                    }
                }
                
                // Fetch follower counts
                const followCountRes = await fetch(`${API_URL}/api/social/${profileAddress}/counts`);
                if (followCountRes.ok) {
                    const countData = await followCountRes.json();
                    setFollowersCount(countData.followers || 0);
                    setFollowingCount(countData.following || 0);
                }
            } catch (error) {
                console.error('Failed to fetch profile:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [profileAddress, connectedAddress, isOwnProfile]);

    const copyAddress = () => {
        navigator.clipboard.writeText(profileAddress);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Address copied!');
    };

    const handleFollow = async () => {
        if (!isConnected) {
            toast.error('Connect wallet to follow');
            return;
        }
        
        try {
            const endpoint = isFollowing 
                ? `${API_URL}/api/social/${connectedAddress}/unfollow/${profileAddress}`
                : `${API_URL}/api/social/${connectedAddress}/follow/${profileAddress}`;
            
            const res = await fetch(endpoint, { method: 'POST' });
            if (res.ok) {
                setIsFollowing(!isFollowing);
                setFollowersCount(prev => isFollowing ? prev - 1 : prev + 1);
                toast.success(isFollowing ? 'Unfollowed' : 'Following!');
            }
        } catch (error) {
            toast.error('Failed to update follow status');
        }
    };

    const shareProfile = () => {
        const url = `${getPublicOrigin()}/profile/${profileAddress}`;
        if (navigator.share) {
            navigator.share({ title: 'Bet4Peer Profile', url });
        } else {
            navigator.clipboard.writeText(url);
            toast.success('Profile link copied!');
        }
    };

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <Skeleton className="h-48 w-full mb-6 bg-white/5" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-64 bg-white/5" />
                    <Skeleton className="h-64 lg:col-span-2 bg-white/5" />
                </div>
            </div>
        );
    }

    const level = gamification?.level || { level: 1, name: 'Novice', color: '#FFFFFF' };
    const xp = gamification?.xp || 0;
    const nextLevelXp = gamification?.next_level_xp;
    const earnedBadges = gamification?.earned_badges || [];
    const userStats = gamification?.stats || stats || {};

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="profile-page">
            {/* Profile Header */}
            <Card className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border-white/5 mb-6 overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[100px]" 
                     style={{ backgroundColor: `${level.color}10` }} />
                <CardContent className="p-6 md:p-8 relative">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                        {/* Avatar/Level Badge */}
                        <div 
                            className="w-24 h-24 rounded-2xl flex items-center justify-center font-bold text-4xl relative"
                            style={{ 
                                backgroundColor: `${level.color}20`,
                                color: level.color,
                                border: `3px solid ${level.color}`
                            }}
                        >
                            {level.level}
                            {earnedBadges.length > 0 && (
                                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center">
                                    <Trophy className="w-4 h-4 text-[#0A0A0C]" />
                                </div>
                            )}
                        </div>
                        
                        {/* Profile Info */}
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <h1 className="text-2xl md:text-3xl font-bold text-white font-mono">
                                    {formatAddress(profileAddress)}
                                </h1>
                                <button onClick={copyAddress} className="text-white/40 hover:text-white">
                                    {copied ? <Check className="w-5 h-5 text-[#00FF66]" /> : <Copy className="w-5 h-5" />}
                                </button>
                            </div>
                            
                            <div className="flex items-center gap-3 mb-4">
                                <Badge 
                                    className="text-sm px-3 py-1"
                                    style={{ backgroundColor: `${level.color}20`, color: level.color }}
                                >
                                    <Star className="w-3 h-3 mr-1" />
                                    {level.name}
                                </Badge>
                                <span className="text-white/50 text-sm">
                                    {xp.toLocaleString()} XP
                                </span>
                                {nextLevelXp && (
                                    <span className="text-white/30 text-sm">
                                        ({nextLevelXp - xp} to next level)
                                    </span>
                                )}
                            </div>
                            
                            {/* XP Progress */}
                            {nextLevelXp && (
                                <div className="max-w-md mb-4">
                                    <Progress 
                                        value={((xp - (level.xp_required || 0)) / (nextLevelXp - (level.xp_required || 0))) * 100} 
                                        className="h-2 bg-white/10" 
                                    />
                                </div>
                            )}
                            
                            {/* Social Stats */}
                            <div className="flex items-center gap-6 text-sm">
                                <div className="text-white/60">
                                    <span className="text-white font-semibold">{followersCount}</span> followers
                                </div>
                                <div className="text-white/60">
                                    <span className="text-white font-semibold">{followingCount}</span> following
                                </div>
                                <div className="text-white/60">
                                    <span className="text-white font-semibold">{earnedBadges.length}</span> badges
                                </div>
                            </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                            {!isOwnProfile && isConnected && (
                                <Button
                                    onClick={handleFollow}
                                    className={isFollowing 
                                        ? "bg-white/10 text-white hover:bg-white/20" 
                                        : "bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52]"
                                    }
                                >
                                    {isFollowing ? (
                                        <><UserMinus className="w-4 h-4 mr-2" />Unfollow</>
                                    ) : (
                                        <><UserPlus className="w-4 h-4 mr-2" />Follow</>
                                    )}
                                </Button>
                            )}
                            <Button variant="outline" className="border-white/10" onClick={shareProfile}>
                                <Share2 className="w-4 h-4" />
                            </Button>
                            <a 
                                href={`https://basescan.org/address/${profileAddress}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button variant="outline" className="border-white/10">
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger 
                        value="overview"
                        className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]"
                    >
                        <Activity className="w-4 h-4 mr-2" />
                        Overview
                    </TabsTrigger>
                    <TabsTrigger 
                        value="badges"
                        className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]"
                    >
                        <Award className="w-4 h-4 mr-2" />
                        Badges ({earnedBadges.length})
                    </TabsTrigger>
                    <TabsTrigger 
                        value="stats"
                        className="data-[state=active]:bg-[#9B6FE8]/10 data-[state=active]:text-[#9B6FE8]"
                    >
                        <PieChart className="w-4 h-4 mr-2" />
                        Statistics
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Quick Stats */}
                        <div className="space-y-4">
                            <Card className="bg-white/[0.02] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm text-white/60">Performance</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Total Bets</span>
                                        <span className="text-white font-semibold">
                                            {(userStats.bets_created || 0) + (userStats.bets_taken || 0)}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Wins</span>
                                        <span className="text-[#00FF66] font-semibold">{userStats.wins || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Losses</span>
                                        <span className="text-red-400 font-semibold">{userStats.losses || 0}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Win Rate</span>
                                        <span className="text-[#FFD700] font-semibold">{userStats.win_rate || 0}%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Best Streak</span>
                                        <span className="text-[#FF6B6B] font-semibold flex items-center gap-1">
                                            <Flame className="w-4 h-4" />
                                            {userStats.max_streak || 0}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                            
                            <Card className="bg-white/[0.02] border-white/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm text-white/60">Volume</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Total Staked</span>
                                        <span className="text-white font-mono">
                                            {(userStats.total_volume || 0).toFixed(4)} ETH
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Chains Used</span>
                                        <span className="text-white flex items-center gap-1">
                                            <Globe className="w-4 h-4 text-[#00CED1]" />
                                            {userStats.chains_used || 0} / 3
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-white/60 text-sm">Oracle Uses</span>
                                        <span className="text-white">{userStats.oracle_resolutions || 0}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        
                        {/* Recent Badges & Activity */}
                        <div className="lg:col-span-2 space-y-4">
                            {/* Recent Badges */}
                            {earnedBadges.length > 0 && (
                                <Card className="bg-white/[0.02] border-white/5">
                                    <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="text-sm text-white/60">Recent Badges</CardTitle>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="text-[#FFD700] text-xs"
                                                onClick={() => setActiveTab('badges')}
                                            >
                                                View All
                                            </Button>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex flex-wrap gap-4">
                                            {earnedBadges.slice(0, 6).map((badge) => (
                                                <BadgeDisplay key={badge.id || badge.name} badge={badge} size="sm" showDescription={false} />
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                            
                            {/* Recent Bets */}
                            <Card className="bg-white/[0.02] border-white/5">
                                <CardHeader className="pb-2">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-sm text-white/60">Recent Activity</CardTitle>
                                        <Link to={`/history?address=${profileAddress}`}>
                                            <Button variant="ghost" size="sm" className="text-[#00FF66] text-xs">
                                                View History
                                            </Button>
                                        </Link>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {recentBets.length === 0 ? (
                                        <div className="text-center py-8 text-white/40">
                                            No recent activity
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {recentBets.slice(0, 5).map((bet) => (
                                                <Link 
                                                    key={bet.id}
                                                    to={`/bet/${bet.id}`}
                                                    className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
                                                >
                                                    <div className={`w-2 h-2 rounded-full ${
                                                        bet.status === 'resolved' ? 'bg-[#00FF66]' :
                                                        bet.status === 'active' ? 'bg-[#FFD700]' :
                                                        bet.status === 'cancelled' ? 'bg-red-400' :
                                                        'bg-white/30'
                                                    }`} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-white text-sm truncate">{bet.description}</div>
                                                        <div className="text-white/40 text-xs capitalize">{bet.status}</div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-white font-mono text-sm">
                                                            {formatETH(bet.creator_stake || 0)} ETH
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                {/* Badges Tab */}
                <TabsContent value="badges" className="mt-6">
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#FFD700]" />
                                Badge Collection
                            </CardTitle>
                            <CardDescription className="text-white/50">
                                {earnedBadges.length} badges earned
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {earnedBadges.length === 0 ? (
                                <div className="text-center py-12">
                                    <Trophy className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                    <p className="text-white/50">No badges earned yet</p>
                                    {isOwnProfile && (
                                        <Link to="/create">
                                            <Button className="mt-4 bg-[#00FF66] text-[#0A0A0C]">
                                                Start Betting to Earn Badges
                                            </Button>
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {earnedBadges.map((badge) => (
                                        <div 
                                            key={badge.id || badge.name}
                                            className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-colors"
                                        >
                                            <BadgeDisplay badge={badge} size="md" showDescription={true} />
                                            {badge.earned_at && (
                                                <div className="mt-2 text-xs text-white/30 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(badge.earned_at).toLocaleDateString()}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Statistics Tab */}
                <TabsContent value="stats" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {stats && (
                            <>
                                <Card className="bg-white/[0.02] border-white/5">
                                    <CardHeader>
                                        <CardTitle className="text-lg text-white">Win/Loss Distribution</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <WinLossChart 
                                            wins={userStats.wins || 0} 
                                            losses={userStats.losses || 0} 
                                        />
                                    </CardContent>
                                </Card>
                                
                                {stats.category_breakdown && (
                                    <Card className="bg-white/[0.02] border-white/5">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white">Category Breakdown</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <CategoryPieChart data={stats.category_breakdown} />
                                        </CardContent>
                                    </Card>
                                )}
                                
                                {stats.monthly_stats && (
                                    <Card className="bg-white/[0.02] border-white/5 lg:col-span-2">
                                        <CardHeader>
                                            <CardTitle className="text-lg text-white">Monthly Performance</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <ProfitChart data={stats.monthly_stats} />
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        )}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
